import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  RefreshCw, Database, HardDrive, Cpu, TrendingUp,
  CheckCircle2, AlertTriangle, AlertOctagon, Sliders, Camera, Rocket,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const RANGE_OPTIONS = [
  { key: "daily", label: "يومي (30 يوم)" },
  { key: "weekly", label: "أسبوعي (12 أسبوع)" },
  { key: "monthly", label: "شهري (12 شهر)" },
];

const SEV_STYLE = {
  healthy: { bg: "bg-green-50 border-green-200", text: "text-green-800", icon: CheckCircle2, iconColor: "text-green-700" },
  warning: { bg: "bg-amber-50 border-amber-200", text: "text-amber-900", icon: AlertTriangle, iconColor: "text-amber-700" },
  critical: { bg: "bg-red-50 border-red-200", text: "text-red-900", icon: AlertOctagon, iconColor: "text-red-700" },
};

export default function AdminSystem() {
  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState("daily");
  const [history, setHistory] = useState({ points: [] });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [capacityOpen, setCapacityOpen] = useState(false);

  const loadHealth = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/system/health");
      setH(data);
    } finally { setLoading(false); }
  }, []);

  const loadHistory = useCallback(async (r) => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/admin/system/history?range=${r}`);
      setHistory(data);
    } finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => { loadHealth(); }, [loadHealth]);
  useEffect(() => { loadHistory(range); }, [range, loadHistory]);
  useEffect(() => {
    const t = setInterval(loadHealth, 60000);
    return () => clearInterval(t);
  }, [loadHealth]);

  const takeSnapshot = async () => {
    try {
      const { data } = await api.post("/admin/system/snapshot");
      if (data.status === "captured") toast.success("تم التقاط لقطة جديدة لليوم.");
      else toast.info("تم التقاط لقطة اليوم مسبقًا.");
      await loadHistory(range);
      await loadHealth();
    } catch (e) {
      toast.error("تعذّر التقاط اللقطة.");
    }
  };

  if (!h) return <div className="p-8 text-center text-foreground/50 text-sm" data-testid="admin-system-loading">جاري تحميل حالة النظام...</div>;

  const rec = h.recommendation || {};
  const cap = h.capacity || {};
  const usage = rec.usage_pct || { db: 0, uploads: 0, memory: 0 };
  const sev = SEV_STYLE[rec.severity] || SEV_STYLE.healthy;
  const SevIcon = sev.icon;

  return (
    <div className="space-y-4" dir="rtl" data-testid="admin-system">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">حالة النظام</h1>
          <p className="text-foreground/60 mt-1 text-sm">
            سعة قاعدة البيانات، تخزين الملفات، اتجاهات النمو، وتوصيات الترقية الذكية.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={takeSnapshot} data-testid="sys-snapshot-btn">
            <Camera className="h-4 w-4 me-1" />التقط لقطة الآن
          </Button>
          <Button size="sm" variant="outline" onClick={() => setCapacityOpen(true)} data-testid="sys-capacity-edit-btn">
            <Sliders className="h-4 w-4 me-1" />ضبط السعة
          </Button>
          <Button size="sm" variant="outline" onClick={loadHealth} disabled={loading} data-testid="sys-refresh">
            <RefreshCw className={`h-4 w-4 me-1 ${loading ? "animate-spin" : ""}`} />تحديث
          </Button>
        </div>
      </div>

      {/* Upgrade recommendation card */}
      <Card className={`p-4 border ${sev.bg}`} data-testid="sys-recommendation">
        <div className="flex items-start gap-3">
          <SevIcon className={`h-6 w-6 shrink-0 ${sev.iconColor}`} />
          <div className="flex-1">
            <div className={`font-semibold ${sev.text}`} data-testid="sys-recommendation-headline">
              {rec.headline}
            </div>
            <div className="text-xs mt-1 text-foreground/70">
              الخطة الحالية: <span className="font-semibold">{rec.current_plan_label}</span>
              {rec.action === "upgrade" && (
                <> · الخطة المقترحة: <span className="font-semibold">{rec.suggested_plan_label}</span></>
              )}
            </div>
            <ul className="text-xs mt-2 space-y-0.5 text-foreground/80">
              {(rec.reasons || []).map((r, i) => <li key={i}>· {r}</li>)}
            </ul>
            {rec.action === "upgrade" && (
              <div className="mt-3 text-[11px] text-foreground/60 bg-white/60 rounded p-2 border border-foreground/5">
                <Rocket className="h-3 w-3 inline me-1" />
                للترقية: اذهب إلى إعدادات Emergent → Plan → اختر <b>{rec.suggested_plan_label}</b>.
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Capacity bars — grand summary */}
      <div className="grid gap-3 md:grid-cols-3">
        <CapacityBar
          testId="cap-db"
          icon={Database}
          iconColor="text-blue-700"
          title="قاعدة البيانات"
          current={h.db.storage_size_mb}
          capacity={cap.db_mb}
          pct={usage.db}
          unit="MB"
          daysUntilFull={rec.days_until_full?.db}
        />
        <CapacityBar
          testId="cap-uploads"
          icon={HardDrive}
          iconColor="text-purple-700"
          title="تخزين الملفات"
          current={h.uploads.total_mb}
          capacity={cap.uploads_mb}
          pct={usage.uploads}
          unit="MB"
          daysUntilFull={rec.days_until_full?.uploads}
        />
        <CapacityBar
          testId="cap-memory"
          icon={Cpu}
          iconColor="text-slate-700"
          title="ذاكرة الخادم"
          current={h.backend.rss_mb || 0}
          capacity={cap.memory_mb}
          pct={usage.memory}
          unit="MB"
        />
      </div>

      {/* Historical growth chart */}
      <Card className="p-4" data-testid="sys-history-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-700" />
            <h3 className="font-semibold text-sm">اتجاهات النمو</h3>
          </div>
          <Tabs value={range} onValueChange={setRange}>
            <TabsList>
              {RANGE_OPTIONS.map(o => (
                <TabsTrigger key={o.key} value={o.key} data-testid={`sys-range-${o.key}`}>{o.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {historyLoading ? (
          <div className="text-center text-xs text-foreground/50 py-10">جاري تحميل السجل...</div>
        ) : (history.points || []).length === 0 ? (
          <div className="text-center text-xs text-foreground/50 py-10">
            لا توجد لقطات بعد. اضغط "التقط لقطة الآن" لبدء التسجيل. النظام يلتقط لقطة يومية تلقائيًا.
          </div>
        ) : (
          <div className="h-64" data-testid="sys-history-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={history.points} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="grad-db" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="grad-up" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#9333ea" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#9333ea" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="grad-mem" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#64748b" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#64748b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} reversed />
                <YAxis tick={{ fontSize: 10 }} label={{ value: "MB", angle: -90, position: "insideLeft", fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: 12, direction: "rtl" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="db_mb" name="DB" stroke="#2563eb" fill="url(#grad-db)" strokeWidth={2} />
                <Area type="monotone" dataKey="uploads_mb" name="ملفات" stroke="#9333ea" fill="url(#grad-up)" strokeWidth={2} />
                <Area type="monotone" dataKey="memory_mb" name="ذاكرة" stroke="#64748b" fill="url(#grad-mem)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="text-[10px] text-foreground/40 mt-2">
          يتم التقاط لقطة تلقائيًا مرة يوميًا. عند مرور فترة، ستظهر الاتجاهات هنا لتساعدك على تقدير موعد الترقية.
        </div>
      </Card>

      {/* Hot collections + activity + external */}
      <div className="grid gap-3 lg:grid-cols-2">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Database className="h-4 w-4 text-blue-700" />
            <h3 className="font-semibold text-sm">أكبر المجموعات في قاعدة البيانات</h3>
          </div>
          <div className="text-[10px] text-foreground/50 mb-2">
            {h.db.objects.toLocaleString("ar-SA")} سجل عبر {h.db.collections} مجموعة · فهارس {h.db.index_size_mb} MB
          </div>
          <div className="space-y-0.5 text-xs">
            {h.db.hot_collections.slice(0, 8).map((c) => (
              <div key={c.collection} className="flex justify-between border-b border-foreground/5 py-1">
                <span className="font-mono text-[11px]">{c.collection}</span>
                <span className="text-foreground/60">{c.count.toLocaleString("ar-SA")}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-emerald-700" />
            <h3 className="font-semibold text-sm">نشاط آخر 24 ساعة</h3>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <Cell label="حسابات جديدة" v={h.activity_24h.signups} />
            <Cell label="Leads" v={h.activity_24h.leads} />
            <Cell label="مشتريات" v={h.activity_24h.purchases_paid} />
            <Cell label="بريد أُرسل" v={h.activity_24h.emails_sent} />
            <Cell label="واتساب أُرسل" v={h.activity_24h.whatsapp_sent} />
            <Cell label="حسابات (7ي)" v={h.activity_24h.signups_7d} />
          </div>

          <div className="mt-3 border-t border-foreground/5 pt-2 text-xs space-y-1">
            {[["Stripe", h.external.stripe_configured], ["Resend", h.external.resend_configured], ["Twilio", h.external.twilio_configured], ["Storage", h.external.storage_configured]].map(([label, ok]) => (
              <div key={label} className="flex justify-between">
                <span>{label}</span>
                <span className={ok ? "text-green-700" : "text-red-700"}>{ok ? "✓ مُهيَّأ" : "✗ غير مُهيَّأ"}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="text-[10px] text-foreground/40 text-center pt-2">
        آخر تحديث: {new Date(h.generated_at).toLocaleString("ar-SA")} · يُحدَّث تلقائيًا كل دقيقة
      </div>

      {/* Capacity edit dialog */}
      <CapacityDialog
        open={capacityOpen}
        onClose={() => setCapacityOpen(false)}
        initial={cap}
        tiers={cap.tiers || {}}
        onSaved={() => { loadHealth(); toast.success("تم حفظ حدود السعة."); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function CapacityBar({ testId, icon: Icon, iconColor, title, current, capacity, pct, unit = "MB", daysUntilFull }) {
  const pctSafe = Math.min(100, Math.max(0, Math.round(pct || 0)));
  const barColor = pctSafe >= 90 ? "bg-red-500" : pctSafe >= 75 ? "bg-amber-500" : "bg-green-500";
  const display = (v) => {
    if (v == null) return "—";
    if (v >= 1024) return `${(v / 1024).toFixed(2)} GB`;
    return `${v.toFixed(v < 10 ? 2 : 1)} ${unit}`;
  };
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${iconColor}`} />
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      <div className="flex items-baseline gap-1">
        <div className="text-2xl font-bold">{display(current)}</div>
        <div className="text-xs text-foreground/50">/ {display(capacity)}</div>
      </div>
      <div className="h-2 rounded bg-foreground/5 mt-2 overflow-hidden">
        <div className={`h-full transition-all ${barColor}`} style={{ width: `${pctSafe}%` }} />
      </div>
      <div className="text-[10px] text-foreground/50 mt-1 flex justify-between">
        <span>{pctSafe}% مُستخدَم</span>
        {daysUntilFull != null && daysUntilFull > 0 && (
          <span className={daysUntilFull <= 30 ? "text-red-600 font-semibold" : daysUntilFull <= 90 ? "text-amber-600" : ""}>
            تقدير: {daysUntilFull} يوم
          </span>
        )}
      </div>
    </Card>
  );
}

function Cell({ label, v }) {
  return (
    <div className="border rounded p-2 bg-white">
      <div className="text-lg font-bold">{v}</div>
      <div className="text-[10px] text-foreground/50">{label}</div>
    </div>
  );
}

function CapacityDialog({ open, onClose, initial, tiers, onSaved }) {
  const [plan, setPlan] = useState(initial?.current_plan || "preview");
  const [dbMb, setDbMb] = useState(initial?.db_mb || 5120);
  const [upMb, setUpMb] = useState(initial?.uploads_mb || 51200);
  const [memMb, setMemMb] = useState(initial?.memory_mb || 2048);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPlan(initial?.current_plan || "preview");
    setDbMb(initial?.db_mb || 5120);
    setUpMb(initial?.uploads_mb || 51200);
    setMemMb(initial?.memory_mb || 2048);
  }, [open, initial]);

  const applyTier = (tierKey) => {
    setPlan(tierKey);
    const t = tiers[tierKey];
    if (t) {
      setDbMb(t.db_mb);
      setUpMb(t.uploads_mb);
      setMemMb(t.memory_mb);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/system/capacity", {
        current_plan: plan,
        db_mb: Number(dbMb),
        uploads_mb: Number(upMb),
        memory_mb: Number(memMb),
      });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error("تعذّر حفظ التغييرات.");
    } finally { setSaving(false); }
  };

  const tierEntries = useMemo(() => Object.entries(tiers || {}), [tiers]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" data-testid="capacity-dialog">
        <DialogHeader>
          <DialogTitle>ضبط حدود السعة</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-xs text-foreground/60 block mb-1">الخطة الحالية</label>
            <Select value={plan} onValueChange={applyTier}>
              <SelectTrigger data-testid="capacity-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {tierEntries.map(([k, t]) => (
                  <SelectItem key={k} value={k}>{t.label_ar}</SelectItem>
                ))}
                <SelectItem value="custom">مخصصة</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-foreground/40 mt-1">
              اختيار خطة يملأ الحدود بالقيم الافتراضية. يمكنك تعديلها يدويًا.
            </p>
          </div>

          <Field label="حد قاعدة البيانات (MB)" value={dbMb} onChange={setDbMb} testId="capacity-db-input" />
          <Field label="حد تخزين الملفات (MB)" value={upMb} onChange={setUpMb} testId="capacity-uploads-input" />
          <Field label="حد ذاكرة الخادم (MB)" value={memMb} onChange={setMemMb} testId="capacity-memory-input" />

          <div className="text-[10px] text-foreground/50 bg-foreground/5 rounded p-2">
            هذه الحدود تُستخدَم لحساب النسب المئوية والتوصيات. لا تغيّر السعة الفعلية للخادم.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="capacity-cancel">إلغاء</Button>
          <Button onClick={save} disabled={saving} data-testid="capacity-save">{saving ? "جاري الحفظ..." : "حفظ"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs text-foreground/60 block mb-1">{label}</label>
      <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}
